import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

export function usePendingInvitations(userEmail) {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(false); // Start with false to not block UI
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userEmail) {
      setInvitations([]);
      setLoading(false);
      return;
    }

    // Check cache first for instant display
    const cacheKey = `pending_invitations_${userEmail}`;
    const cachedData = sessionStorage.getItem(cacheKey);
    const cacheTimestamp = sessionStorage.getItem(`${cacheKey}_timestamp`);
    const cacheAge = cacheTimestamp ? Date.now() - parseInt(cacheTimestamp) : Infinity;

    // Use cache if less than 5 minutes old
    if (cachedData && cacheAge < 5 * 60 * 1000) {
      try {
        const cached = JSON.parse(cachedData);
        setInvitations(cached);
      } catch (e) {
        console.error('Error parsing cached invitations:', e);
      }
    }

    const findPendingInvitations = async () => {
      try {
        setError(null);

        // Use Cloud Function to get pending invitations
        const getPendingInvitations = httpsCallable(functions, 'get_pending_invitations');
        const result = await getPendingInvitations();

        if (result.data?.success) {
          const newInvitations = result.data.invitations || [];
          setInvitations(newInvitations);
          // Cache the results
          sessionStorage.setItem(cacheKey, JSON.stringify(newInvitations));
          sessionStorage.setItem(`${cacheKey}_timestamp`, Date.now().toString());
        } else {
          setInvitations([]);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error checking pending invitations:', err);
        setError(err);
        setInvitations([]);
        setLoading(false);
      }
    };

    // Fetch in background (non-blocking)
    findPendingInvitations();

    // Poll for updates every 60 seconds (reduced from 30 to save bandwidth)
    const interval = setInterval(findPendingInvitations, 60000);

    return () => {
      clearInterval(interval);
    };
  }, [userEmail]);

  return {
    invitations,
    loading,
    error,
    hasPendingInvitations: invitations.length > 0
  };
}